/**
 * Pinterest Integration
 * Supports both browser-based (sales channel) and server-side (Lambda) modes.
 * Configures Enhanced Match with pre-hashed customer data.
 *
 * Standalone version for co-aurora.
 * Ported from shopper/src/scripts/lib/integrations/pinterest.js
 */
(function() {
  'use strict';

  function Pinterest(config) {
    this.tagId = config.tagId;
    this.serverSideMode = false;
    this.serverSideEnhancedMatch = {};
    window.TrackingBase.call(this, Object.assign({
      name: 'Pinterest',
      enableConversionsAPI: true,
      enableEnhancedMatch: true,
      enableDeduplication: true,
      serverSideMode: false,
      lambdaEndpoint: ''
    }, config));
  }

  Pinterest.prototype = Object.create(window.TrackingBase.prototype);
  Pinterest.prototype.constructor = Pinterest;

  /* ---- Initialize ---- */

  Pinterest.prototype.initialize = function() {
    if (this.config.serverSideMode) {
      this._initializeServerSideMode();
      return;
    }
    this._checkForPintrkPeriodically();
  };

  Pinterest.prototype._checkForPintrkPeriodically = function() {
    var self = this;

    if (window.pintrk && this._isPintrkAlreadyInitialized()) {
      this._completePintrkSetup();
      return;
    }

    var attempts = 0;
    var maxAttempts = 100;

    var interval = setInterval(function() {
      attempts++;
      if (window.pintrk && self._isPintrkAlreadyInitialized()) {
        self.log('Sales channel Pinterest detected after ' + (attempts * 100) + 'ms');
        self._completePintrkSetup();
        clearInterval(interval);
        return;
      }
      if (attempts >= maxAttempts) {
        if (!window.pintrk) {
          self.log('Pinterest not available - switching to server-side');
          self.config.serverSideMode = true;
          self._initializeServerSideMode();
        }
        clearInterval(interval);
      }
    }, 100);
  };

  Pinterest.prototype._isPintrkAlreadyInitialized = function() {
    try {
      if (window.pintrk && window.pintrk.loaded) return true;
      if (window.pintrk && window.pintrk.queue && window.pintrk.queue.length > 0) return true;
      if (document.querySelector('script[src*="pinimg.com/ct/core.js"]') && window.pintrk) return true;
      return false;
    } catch (e) { return false; }
  };

  Pinterest.prototype._completePintrkSetup = function() {
    this.tagLoaded = true;
    if (!this._isPintrkAlreadyInitialized()) return;
    this._configureEnhancedMatch();
  };

  Pinterest.prototype._initializeServerSideMode = function() {
    this.tagLoaded = true;
    this.serverSideMode = true;
    this._configureEnhancedMatchForServerSide();
    this.log('Pinterest server-side mode initialized');
  };

  /* ---- Enhanced Match ---- */

  Pinterest.prototype._configureEnhancedMatch = function() {
    try {
      var emd = {};

      /* IP */
      if (window.AttributionManager) {
        var sess = window.AttributionManager.getSessionData();
        if (sess && sess.client_ip) {
          emd.client_ip_address = sess.client_ip;
          emd.client_user_agent = navigator.userAgent;
        }
      }

      /* Prefer pre-hashed data */
      var customer = window.theme && window.theme.customer;
      if (customer && customer.hashed) {
        var h = customer.hashed;
        if (h.email) emd.em = h.email;
        if (h.phone) emd.ph = h.phone;
        if (h.first_name) emd.fn = h.first_name;
        if (h.last_name) emd.ln = h.last_name;
        if (h.city) emd.ct = h.city;
        if (h.state) emd.st = h.state;
        if (h.postal_code) emd.zp = h.postal_code;
        if (h.country) emd.country = h.country;
        if (h.external_id) emd.external_id = h.external_id;
      }

      if (Object.keys(emd).length > 0 && window.pintrk) {
        window.pintrk('set', { np: 'shopify' });
        window.pintrk('load', this.tagId, emd);
        this.log('Enhanced Match configured', { fields: Object.keys(emd) });
      }
    } catch (err) {
      this.warn('Failed to configure Enhanced Match', err);
    }
  };

  Pinterest.prototype._configureEnhancedMatchForServerSide = function() {
    var cd = this.getCustomerData();
    if (!cd || Object.keys(cd).length === 0) return;
    this.serverSideEnhancedMatch = {
      email: cd.email,
      phone: cd.phone,
      firstName: cd.first_name,
      lastName: cd.last_name,
      city: cd.city,
      state: cd.state,
      postalCode: cd.postal_code,
      country: cd.country,
      customerId: cd.customer_id
    };
  };

  /* ---- Track ---- */

  Pinterest.prototype.track = function(eventName, eventData, options) {
    options = options || {};

    if (this.serverSideMode) {
      return this._trackServerSide(eventName, eventData, options);
    }

    if (!this.tagLoaded && window.pintrk) this._completePintrkSetup();
    if (!this.tagLoaded || !window.pintrk) {
      this.config.serverSideMode = true;
      this._initializeServerSideMode();
      return this._trackServerSide(eventName, eventData, options);
    }

    this._configureEnhancedMatch();

    var pData = this._formatEventDataForPinterest(eventData);
    if (options.eventID) pData.event_id = options.eventID;

    var cd = this.getCustomerData();
    if (cd.customer_id && !pData.external_id) pData.external_id = cd.customer_id.toString();

    window.pintrk('track', eventName, pData);
    this.log('Tracked ' + eventName, { eventId: options.eventID, mode: 'browser' });
  };

  Pinterest.prototype._trackServerSide = function(eventName, eventData, options) {
    var self = this;
    var endpoint = this.config.lambdaEndpoint;
    if (!endpoint) { this.warn('No Lambda endpoint configured'); return Promise.resolve(); }

    var payload = {
      eventId: options.eventID || this.generateEventId(),
      eventName: eventName,
      timestamp: Date.now(),
      source: 'pinterest_integration',
      eventData: Object.assign({}, this.serverSideEnhancedMatch, {
        value: eventData.value,
        currency: eventData.currency || 'USD',
        pageUrl: window.location.href,
        pageTitle: document.title
      })
    };

    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function(resp) {
      if (resp.ok) self.log('Server-side tracked ' + eventName);
    }).catch(function(err) {
      self.warn('Server-side tracking failed', err);
    });
  };

  Pinterest.prototype._formatEventDataForPinterest = function(eventData) {
    var f = {};
    if (eventData.value !== undefined) f.value = String(eventData.value);
    if (eventData.currency) f.currency = eventData.currency;
    if (eventData.order_quantity) f.order_quantity = eventData.order_quantity;
    if (eventData.order_id) f.order_id = eventData.order_id;
    if (eventData.product_name) f.product_name = eventData.product_name;
    if (eventData.product_id) f.product_id = String(eventData.product_id);
    if (eventData.product_category) f.product_category = eventData.product_category;
    if (eventData.search_query) f.search_query = eventData.search_query;

    if (eventData.line_items && Array.isArray(eventData.line_items)) {
      f.line_items = eventData.line_items.map(function(item) {
        return {
          product_name: item.product_name || item.item_name,
          product_id: String(item.product_id || item.item_id),
          product_price: String(item.price || item.item_price),
          product_quantity: item.quantity
        };
      });
    }

    return f;
  };

  /* Export */
  window.TrackingPinterest = Pinterest;
})();
